import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FiSave, FiTrash2, FiX, FiDownload, FiUpload, FiFile } from "react-icons/fi";
import { BarLoader } from "react-spinners";
import Swal from "sweetalert2";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EditDoc = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [newFilePreview, setNewFilePreview] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [descriptionError, setDescriptionError] = useState(false);
  const quillRef = useRef(null);

  const [documentData, setDocumentData] = useState({
    title: "",
    description: "",
    type: "guideline",
    file: "",
  });

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/doc/${id}`,
          { withCredentials: true }
        );
        setDocumentData(response.data.data);
        
        // Set initial character count
        if (response.data.data.description) {
          const plainText = response.data.data.description.replace(/<[^>]*>/g, '');
          setCharCount(plainText.length);
        }
        
        if (response.data.data.file) {
          setCurrentFile({
            url: `${process.env.REACT_APP_API_URL}/${response.data.data.file}`,
            name: response.data.data.file.split("/").pop()
          });
        }
        setLoading(false);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/login");
        } else {
          toast.error(err.response?.data?.error || "Failed to load document");
          navigate("/admin/documents");
        }
      }
    };

    fetchDocument();
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDocumentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (value) => {
    const plainText = value.replace(/<[^>]*>/g, '');
    setCharCount(plainText.length);
    
    if (plainText.length > 1200) {
      setDescriptionError(true);
    } else {
      setDescriptionError(false);
    }

    setDocumentData(prev => ({
      ...prev,
      description: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setNewFile(selectedFile);

      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => {
          setNewFilePreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setNewFilePreview(null);
      }
    }
  };

  const removeNewFile = () => {
    setNewFile(null);
    setNewFilePreview(null);
    document.getElementById("file-upload").value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (descriptionError) {
      toast.error('Description must be 1200 characters or less');
      return;
    }
    
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("title", documentData.title);
      formData.append("description", documentData.description);
      formData.append("type", documentData.type);
      if (newFile) {
        formData.append("file", newFile);
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/doc/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Document updated successfully");
      navigate("/admin/doc");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update document");
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/doc/${id}`, {
          withCredentials: true,
        });
        toast.success("Document deleted successfully");
        navigate("/admin/doc");
      } catch (err) {
        toast.error(err.response?.data?.error || "Failed to delete document");
      }
    }
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike', 
    'align', 'list', 'indent', 'link'
  ];

  // Focus editor when component mounts
  useEffect(() => {
    if (quillRef.current && !loading) {
      setTimeout(() => {
        const quill = quillRef.current.getEditor();
        quill.focus();
      }, 300);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <BarLoader color="#4F46E5" size={50} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Edit Document</h1>
          <button
            onClick={() => navigate("/admin/doc")}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Back to Documents
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={documentData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="type"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Document Type *
                  </label>
                  <input
                    type="text"
                    id="type"
                    name="type"
                    value={documentData.type}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                    placeholder="Enter document type (e.g., Report, Guideline)"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={documentData.description}
                    onChange={handleDescriptionChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter document description (max 1200 characters)..."
                    className="quill-editor"
                  />
                </div>
                <div className={`text-right text-sm mt-1 ${
                  charCount > 1200 ? 'text-red-500' : 'text-gray-500'
                }`}>
                  {charCount} / 1200 characters
                  {descriptionError && (
                    <span className="ml-2">(Maximum 1200 characters allowed)</span>
                  )}
                </div>
              </div>

              {/* Current File Section */}
              {currentFile && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Current Document</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiDownload className="text-gray-500 mr-2" />
                      <a
                        href={currentFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {currentFile.name}
                      </a>
                    </div>
                    <span className="text-xs text-gray-500">(Existing file)</span>
                  </div>
                </div>
              )}

              {/* New File Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {newFile ? "New Document File" : "Replace Document File"}
                </label>
                <div className="mt-1 flex items-center">
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FiUpload className="mr-2" />
                    {newFile ? "Change File" : "Upload New File"}
                  </label>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                  
                  {newFile && (
                    <div className="ml-4 flex items-center">
                      <div className="flex items-center">
                        {newFilePreview ? (
                          <img 
                            src={newFilePreview} 
                            alt="Preview" 
                            className="h-8 w-8 object-cover rounded mr-2"
                          />
                        ) : (
                          <FiFile className="text-gray-500 mr-2" />
                        )}
                        <span className="text-gray-700">{newFile.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={removeNewFile}
                        className="ml-2 text-red-600 hover:text-red-800 p-1"
                        title="Remove file"
                      >
                        <FiX />
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Upload a new file to replace the current document (PDF, DOCX, etc.)
                </p>
              </div>

              {/* Preview for new image files */}
              {newFilePreview && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">New File Preview:</p>
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <img 
                      src={newFilePreview} 
                      alt="File preview" 
                      className="max-w-full h-auto max-h-48 mx-auto"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <FiTrash2 className="mr-2" />
                Delete Document
              </button>
              <div className="space-x-3">
                <button
                  type="button"
                  onClick={() => navigate("/admin/documents")}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiX className="mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || descriptionError}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <BarLoader
                        color="#ffffff"
                        height={5}
                        width={20}
                        className="mr-2"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <style jsx global>{`
        .quill-editor .ql-container {
          min-height: 150px;
          font-size: 16px;
          line-height: 1.6;
        }
        .quill-editor .ql-editor {
          min-height: 150px;
        }
        .quill-editor .ql-toolbar {
          border-radius: 4px 4px 0 0;
          border: 1px solid #e2e8f0;
          border-bottom: none;
        }
        .quill-editor .ql-container {
          border-radius: 0 0 4px 4px;
          border: 1px solid #e2e8f0;
        }
        .quill-editor .ql-container.ql-snow {
          border-top: none;
        }
      `}</style>
    </div>
  );
};

export default EditDoc;